import React, { createContext, useContext, useMemo, useState } from 'react';
import type { FC } from 'react';

export type DocumentUpdateStatusContextType = {
	documentUpdated: boolean | null;
	documentNeedsUpdate: boolean | null;
	publishedDocumentVersion: number;
	currentDocumentVersion: number;
};

export type DocumentUpdateStatusDispatchContextType = {
	setDocumentUpdated: (documentUpdated: boolean) => void;
	setDocumentNeedsUpdate: (documentNeedsUpdate: boolean) => void;
	setPublishedDocumentVersion: (publishedDocumentVersion: number) => void;
	setCurrentDocumentVersion: (currentDocumentVersion: number) => void;
};

export const DocumentUpdatedContext = createContext<DocumentUpdateStatusContextType>({
	documentUpdated: false,
	documentNeedsUpdate: false,
	publishedDocumentVersion: 0,
	currentDocumentVersion: 0,
});
DocumentUpdatedContext.displayName = 'DocumentUpdateStatusContext';

export const DocumentUpdatedDispatchContext =
	createContext<DocumentUpdateStatusDispatchContextType>({
		setDocumentUpdated: () => {},
		setDocumentNeedsUpdate: () => {},
		setPublishedDocumentVersion: () => {},
		setCurrentDocumentVersion: () => {},
	});
DocumentUpdatedDispatchContext.displayName = 'DocumentUpdateStatusDispatchContext';

type DocumentUpdateStatusProviderProps = {
	children: JSX.Element;
};

export const DocumentUpdateStatusProvider: FC<DocumentUpdateStatusProviderProps> = ({
	children,
}) => {
	const [documentUpdated, setDocumentUpdated] = useState(false);
	const [documentNeedsUpdate, setDocumentNeedsUpdate] = useState(false);
	const [publishedDocumentVersion, setPublishedDocumentVersion] = useState(0);
	const [currentDocumentVersion, setCurrentDocumentVersion] = useState(0);

	const providerValue = useMemo(
		() => ({
			documentUpdated,
			documentNeedsUpdate,
			publishedDocumentVersion,
			currentDocumentVersion,
		}),
		[documentUpdated, documentNeedsUpdate, publishedDocumentVersion, currentDocumentVersion],
	);

	const dispatchProviderValue = useMemo(
		() => ({
			setDocumentUpdated,
			setDocumentNeedsUpdate,
			setPublishedDocumentVersion,
			setCurrentDocumentVersion,
		}),
		[
			setDocumentUpdated,
			setDocumentNeedsUpdate,
			setPublishedDocumentVersion,
			setCurrentDocumentVersion,
		],
	);

	return (
		<DocumentUpdatedContext.Provider value={providerValue}>
			<DocumentUpdatedDispatchContext.Provider value={dispatchProviderValue}>
				{children}
			</DocumentUpdatedDispatchContext.Provider>
		</DocumentUpdatedContext.Provider>
	);
};

export const useDocumentUpdateStatus = () => {
	return useContext(DocumentUpdatedContext);
};

export const useDocumentUpdateStatusDispatch = () => {
	return useContext(DocumentUpdatedDispatchContext);
};
